import {
  Card,
  CardBody,
  Row,
  Col,
  Breadcrumb,
  BreadcrumbItem,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import ReactTable from "components/ReactTable/ReactTable.js";
import { useMemo, React, useState, useEffect, useContext } from "react";
import { NavLink } from "react-router-dom";
import { FaCheckCircle } from "react-icons/fa";
import { useHistory } from "react-router-dom";
import ReactBSAlert from "react-bootstrap-sweetalert";
import { MainContext } from "context/MainContext.js";

import { faHome } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Utilities from "components/Utils/utilities";

const UserList = () => {
  const history = useHistory();
  const [alert, setAlert] = useState(null);
  const privileges = useContext(MainContext);

  useEffect(() => {
    if (!Utilities.hasAccess(privileges["admin.users.display"])) {
      history.push({
        pathname: "/",
      });
    } else if (Utilities.hasAgreedTerms() == "false") {
      history.push({
        pathname: "/auth/terms",
      });
    }
  }, []);

  const [forceRefreshCount, setForceRefreshCount] = useState(0);

  const deleteUserConfirm = (username) => {
    setAlert(
      <ReactBSAlert
        warning
        style={{ display: "block", marginTop: "-100px" }}
        title="Are you sure you want to delete this user ?"
        onConfirm={() => {
          deleteUser(username);
        }}
        onCancel={() => hideAlert()}
        confirmBtnBsStyle="danger"
        cancelBtnBsStyle="info"
        confirmBtnText="Yes, delete it!"
        cancelBtnText="Cancel"
        showCancel
        btnSize=""
      >
        You will not be able to recover the user '{username}'
      </ReactBSAlert>,
    );
  };

  const deleteUser = (username) => {
    const start = Date.now();

    // Call the API
    fetch("/api/v1/user/delete", {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        sessionId: sessionStorage.getItem("sessionId"),
      },
      body: JSON.stringify({
        username: username,
      }),
    })
      .then((response) => response.json())
      .then((actualData) => {
        if (actualData.error_code) {
          showMessage("Error!", actualData.error_code, "error");
        } else {
          showMessage("User Deleted!", "User deleted successfully", "success");
          setForceRefreshCount((prevValue) => ++prevValue);
        }
      })
      .catch((err) => {
        console.log(err.message);
      });
  };

  const showMessage = (title, message, type) => {
    if (type == "success") {
      setAlert(
        <ReactBSAlert
          success
          style={{ display: "block", marginTop: "-100px" }}
          title={title}
          onConfirm={() => hideAlert()}
          onCancel={() => hideAlert()}
          confirmBtnBsStyle="info"
          btnSize=""
        >
          {message}
        </ReactBSAlert>,
      );
    } else if (type == "error") {
      setAlert(
        <ReactBSAlert
          error
          style={{ display: "block", marginTop: "-100px" }}
          title={title}
          onConfirm={() => hideAlert()}
          onCancel={() => hideAlert()}
          confirmBtnBsStyle="info"
          btnSize=""
        >
          {message}
        </ReactBSAlert>,
      );
    }
  };

  const redirectToUserEditPage = (username) => {
    const location = {
      pathname: "/admin/users/edit",
      state: {
        value: username ? "username||" + username : "",
        mode: username ? "edit" : "create",
      },
    };
    history.push(location);
  };

  const setUserDevice = (username) => {
    setAlert(
      <ReactBSAlert
        warning
        style={{ display: "block", marginTop: "-100px" }}
        title="Only 1 user can be set as device, are you sure you want to use this user ?"
        onConfirm={() => {
          setAsDevice(username);
        }}
        onCancel={() => hideAlert()}
        confirmBtnBsStyle="danger"
        cancelBtnBsStyle="info"
        confirmBtnText="Yes, set it!"
        cancelBtnText="Cancel"
        showCancel
        btnSize=""
      ></ReactBSAlert>,
    );
  };

  const setAsDevice = (username) => {
    // Call the API
    fetch("/api/v1/user/setDevice", {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        sessionId: sessionStorage.getItem("sessionId"),
      },
      body: JSON.stringify({
        username: username,
      }),
    })
      .then((response) => response.json())
      .then((actualData) => {
        if (actualData.error_code) {
          showMessage("Error!", actualData.error_code, "error");
        } else {
          showMessage(
            "Device Set!",
            "The user is now of type 'Device'",
            "success",
          );
          setForceRefreshCount((prevValue) => ++prevValue);
        }
      })
      .catch((err) => {
        console.log(err.message);
      });
  };

  const hideAlert = () => {
    setAlert(null);
  };

  const redirectToPasswordEditPage = (username) => {
    const location = {
      pathname: "/admin/users/edit",
      state: {
        value: username ? "username||" + username : "",
        mode: "password",
      },
    };
    history.push(location);
  };

  const columns = useMemo(
    () => [
      {
        Header: "User Name",
        accessor: "username",
        sortBy: "username",
        flex: "1 1 0",
      },
      {
        Header: "Full Name",
        accessor: "fullname",
        sortBy: "fullname",
        flex: "1 1 0",
      },
      {
        Header: "User Inactivity Session Timeout",
        accessor: "sessionTimeout",
        sortBy: "sessionTimeout",
        flex: "1 1 0",
      },
      {
        Header: "Privilege level",
        accessor: "privilegeLevel",
        sortBy: "privilegeLevel",
        flex: "1 1 0",
      },
      {
        Header: "Time Zone",
        accessor: "timezone",
        sortBy: "timezone",
        flex: "1 1 0",
      },
      {
        Header: "Enabled",
        accessor: "enabled",
        sortBy: "enabled",
        Cell: (props) => {
          return props.value ? (
            <FaCheckCircle className="text-primary tableIconFontSize" />
          ) : (
            ""
          );
        },
        flex: "1 1 0",
      },
      {
        Header: "Device",
        accessor: "isDevice",
        sortBy: "isDevice",
        Cell: (props) => {
          return props.value ? (
            <FaCheckCircle className="text-primary tableIconFontSize" />
          ) : (
            ""
          );
        },
        flex: "1 1 0",
      },
      {
        Header: "Radius",
        accessor: "isRadius",
        sortBy: "isRadius",
        Cell: (props) => {
          return props.value ? (
            <FaCheckCircle className="text-primary tableIconFontSize" />
          ) : (
            ""
          );
        },
        flex: "1 1 0",
      },
      {
        Header: "",
        accessor: "actions",
        Cell: (props) => {
          const { username, isRadius } = props.row.original;

          const canEdit = Utilities.hasAccess(privileges["admin.users.edit"]);
          const canEditPassword =
            !isRadius &&
            Utilities.hasAccess(privileges["admin.users.password.edit"]);
          const canDelete = Utilities.hasAccess(
            privileges["admin.users.delete"],
          );

          if (!canEdit && !canEditPassword && !canDelete) return null;

          return (
            <UncontrolledDropdown direction="down">
              <DropdownToggle
                caret
                color="info"
                style={{ padding: "6px 14px" }}
              >
                Actions
              </DropdownToggle>
              <DropdownMenu container="body">
                {canEdit && (
                  <DropdownItem
                    onClick={() => redirectToUserEditPage(username)}
                  >
                    Edit User
                  </DropdownItem>
                )}
                {canEditPassword && (
                  <DropdownItem
                    onClick={() => redirectToPasswordEditPage(username)}
                  >
                    Change Password
                  </DropdownItem>
                )}
                {canDelete && (
                  <>
                    <DropdownItem divider />
                    <DropdownItem onClick={() => deleteUserConfirm(username)}>
                      Delete User
                    </DropdownItem>
                  </>
                )}
              </DropdownMenu>
            </UncontrolledDropdown>
          );
        },
        flex: "1 1 0",
        padding: "0px",
        sortable: false,
        filterable: false,
      },
    ],
    [],
  );

  return (
    <div className="content">
      {alert}
      <Row>
        <Col md="12">
          <Card>
            <CardBody>
              <Breadcrumb>
                <BreadcrumbItem>
                  <NavLink
                    to={{
                      pathname: "/admin/dashboard",
                    }}
                  >
                    <FontAwesomeIcon icon={faHome} />
                  </NavLink>
                </BreadcrumbItem>
                <BreadcrumbItem>Users</BreadcrumbItem>
              </Breadcrumb>
            </CardBody>
          </Card>
          <Card>
            <CardBody>
              <ReactTable
                query="/api/v1/user/list"
                columns={columns}
                title="Users"
                className="-striped -highlight primary-pagination"
                forceRefresh={forceRefreshCount}
              />
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default UserList;
